import { colors, fonts } from "../../style";
import { BackLink } from "./buttons";
import { ui } from "./styles";

// Page chrome shared by every screen inside the shell. Responsive padding
// lives in index.css (.sk-page) since inline styles can't do media queries.

export function Page({ children, maxWidth = 1200, gap = 16, style }) {
  return (
    <div
      className="sk-page"
      style={{
        width: "100%",
        maxWidth,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap,
        fontFamily: fonts.system,
        color: colors.textStrong,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// eyebrow: small muted line above the title (e.g. "Admin").
// back: { to | onClick, label } renders a BackLink in place of the eyebrow.
export function PageHeader({
  eyebrow,
  back,
  title,
  subtitle,
  actions,
  children,
}) {
  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px 16px",
        flexWrap: "wrap",
      }}
    >
      <div style={{ flex: "1 1 240px", minWidth: 0 }}>
        {back ? (
          <BackLink to={back.to} onClick={back.onClick}>
            {back.label}
          </BackLink>
        ) : (
          eyebrow && (
            <div style={{ ...ui.muted, fontSize: "13px" }}>{eyebrow}</div>
          )
        )}
        <h1 style={ui.pageTitle}>{title}</h1>
        {subtitle && (
          <div style={{ ...ui.muted, marginTop: "4px", fontSize: "14px" }}>
            {subtitle}
          </div>
        )}
      </div>
      {children}
      {actions && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          {actions}
        </div>
      )}
    </header>
  );
}

const cardVariants = {
  // Default content card
  surface: { background: colors.surface },
  // Darker panel, e.g. a filter bar
  panel: { background: colors.sidebar },
};

// flush: no padding, for tables and lists that run edge to edge.
// title/actions: a SectionLabel row at the top. title or gap stacks the
// children in a column `gap` px apart (default 8).
// Never add overflow here: row dropdowns inside tables would be clipped.
export function Card({
  variant = "surface",
  flush,
  title,
  actions,
  gap,
  children,
  style,
  as: As = "div",
  ...props
}) {
  const stacked = title != null || gap != null;
  return (
    <As
      style={{
        ...ui.card,
        ...cardVariants[variant],
        ...(flush ? { padding: 0 } : null),
        ...(stacked
          ? { display: "flex", flexDirection: "column", gap: gap ?? 8 }
          : null),
        ...style,
      }}
      {...props}
    >
      {title != null &&
        (actions ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <SectionLabel as="span" style={{ flex: 1 }}>
              {title}
            </SectionLabel>
            {actions}
          </div>
        ) : (
          <SectionLabel as="span">{title}</SectionLabel>
        ))}
      {children}
    </As>
  );
}

// Small uppercase label (card headings, list groups)
export function SectionLabel({ children, as: As = "div", style }) {
  return <As style={{ ...ui.label, ...style }}>{children}</As>;
}

// Key/value card. Items with a falsy value are left out.
export function DetailCard({ title, items, style }) {
  return (
    <Card title={title} style={{ fontSize: "13px", ...style }}>
      <dl
        style={{
          margin: 0,
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          gap: "6px 14px",
        }}
      >
        {items
          .filter((item) => item.value)
          .map((item) => (
            <div key={item.label} style={{ display: "contents" }}>
              <dt style={ui.muted}>{item.label}</dt>
              <dd style={{ margin: 0, overflowWrap: "anywhere" }}>
                {item.value}
              </dd>
            </div>
          ))}
      </dl>
    </Card>
  );
}

// Small heading for a section inside a page or card
export function SectionTitle({ children, style }) {
  return <h2 style={{ ...ui.sectionTitle, ...style }}>{children}</h2>;
}
